import type {Mentionable, Reference, SavedReply} from '@primer/react/experimental'
import {type ReactNode, createContext, useContext, useMemo} from 'react'
import {useUploadFile} from './api/file-upload'
import {useGetPreview} from './api/preview'
import {useGetSavedReplies} from './api/saved-replies'
import {useGetMentionSuggestions} from './api/suggestions/mention-suggestions'
import {useGetReferenceSuggestions} from './api/suggestions/reference-suggestions'
import type {MarkdownSubject as ApiMarkdownSubject} from './api/types'
import type {MarkdownSubject} from './subject'

type MarkdownContextData = {
  getMentionSuggestions: () => Promise<Mentionable[]>
  getReferenceSuggestions: () => Promise<Reference[]>
  getSavedReplies: () => Promise<SavedReply[]>
  renderPreview: (markdown: string) => Promise<string>
  uploadFile: (file: File) => Promise<{url: string; file: File}>
}

const MarkdownContext = createContext<MarkdownContextData | null>(null)

interface MarkdownProviderProps {
  children: ReactNode
  subject?: MarkdownSubject
}

export const MarkdownProvider = ({children, subject}: MarkdownProviderProps) => {
  const apiSubject: Partial<ApiMarkdownSubject> =
    !subject || 'projectId' in subject
      ? {}
      : {
          subjectId: subject.id?.databaseId,
          subjectRepoId: subject.repository.databaseId,
          subjectType: subject.type === 'issue_comment' ? 'issue' : subject.type,
        }
  const projectId = subject && 'projectId' in subject ? subject.projectId.toString() : undefined

  const getMentionSuggestions = useGetMentionSuggestions(apiSubject)
  const getReferenceSuggestions = useGetReferenceSuggestions(apiSubject)

  const getSavedReplies = useGetSavedReplies()

  const renderPreview = useGetPreview(apiSubject)

  const uploadFile = useUploadFile(apiSubject.subjectRepoId?.toString(), projectId)

  const contextValue: MarkdownContextData = useMemo(() => {
    return {
      getMentionSuggestions,
      getReferenceSuggestions,
      getSavedReplies,
      renderPreview,
      uploadFile,
    }
  }, [getMentionSuggestions, getReferenceSuggestions, getSavedReplies, renderPreview, uploadFile])

  return <MarkdownContext.Provider value={contextValue}>{children}</MarkdownContext.Provider>
}

export const useMarkdownContext = () => {
  const context = useContext(MarkdownContext)
  if (!context) {
    throw new Error('useMarkdownContext must be used within a MarkdownProvider.')
  }

  return context
}

try{ MarkdownContext.displayName ||= 'MarkdownContext' } catch {}
try{ MarkdownProvider.displayName ||= 'MarkdownProvider' } catch {}